import { Box, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

type Props = {
  label: string;
  value: string | number;
  hint?: string;
  icon?: ReactNode;
  color?: "primary" | "success" | "warning" | "error" | "info" | "default";
  progress?: number; // 0..100
};

const PALETTE: Record<NonNullable<Props["color"]>, string> = {
  primary: "primary.main",
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
  info: "info.main",
  default: "text.secondary",
};

export function KpiCard({ label, value, hint, icon, color = "primary", progress }: Props) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {icon && (
            <Box sx={{ color: PALETTE[color], display: "flex", alignItems: "center", mt: 0.5 }}>
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: PALETTE[color], lineHeight: 1.2, mt: 0.25 }}
            >
              {value}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {hint}
              </Typography>
            )}
            {progress !== undefined && (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, progress))}
                color={color === "default" ? undefined : color}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
